import React, { useState } from 'react';
import { MAILERLITE_CONFIG } from '../config/mailerlite';

interface MailerLiteSignupProps {
  title?: string;
  description?: string;
  colorScheme?: number;
}

const MailerLiteSignup: React.FC<MailerLiteSignupProps> = ({
  title = MAILERLITE_CONFIG.FORM_TITLE,
  description = MAILERLITE_CONFIG.FORM_DESCRIPTION,
  colorScheme = 0
}) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Helper function to get color scheme classes
  const getColorScheme = () => {
    switch (colorScheme) {
      case 0: // Professional (Gray)
        return {
          border: 'border-slate-300',
          focus: 'focus:ring-slate-500',
          button: 'bg-slate-600 hover:bg-slate-700'
        };
      case 1: // Sophisticated Purple
        return {
          border: 'border-purple-300',
          focus: 'focus:ring-purple-500',
          button: 'bg-purple-600 hover:bg-purple-700'
        };
      case 2: // Case File (Navy)
        return {
          border: 'border-blue-300',
          focus: 'focus:ring-blue-500',
          button: 'bg-slate-800 hover:bg-slate-900'
        };
      default:
        return {
          border: 'border-slate-300',
          focus: 'focus:ring-slate-500',
          button: 'bg-slate-600 hover:bg-slate-700'
        };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    try {
      // Use Netlify Functions for newsletter signup
      // In development, use the Netlify dev server port (8888)
      const isDevelopment = process.env.NODE_ENV === 'development';
      const baseUrl = isDevelopment ? 'http://localhost:8888' : '';
      const response = await fetch(`${baseUrl}/.netlify/functions/newsletter-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        setMessage(data.message);
        setEmail('');
        setFirstName('');
        setLastName('');
      } else {
        setMessage(data.message);
        setIsSuccess(false);
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      setMessage(MAILERLITE_CONFIG.ERROR_MESSAGES.NETWORK_ERROR);
      setIsSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`bg-white bg-opacity-95 rounded-lg p-6 border-2 shadow-lg mb-6 w-full ${getColorScheme().border}`}>
      <div className="text-center mb-4">
        <h3 className="text-xl font-oswald font-bold text-gray-900 mb-2">
          {title}
        </h3>
        <p className="text-gray-700 font-lora text-sm">
          {description}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 bg-white ${getColorScheme().focus}`}
              placeholder="Enter your first name"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 bg-white ${getColorScheme().focus}`}
              placeholder="Enter your last name"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={`w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-900 bg-white ${getColorScheme().focus}`}
            placeholder="Enter your email address"
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !email}
          className={`w-full px-6 py-3 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg font-semibold text-white shadow-lg transition-colors duration-200 ${getColorScheme().button}`}
        >
          {isSubmitting ? 'Subscribing...' : 'Subscribe to Newsletter'}
        </button>

        {message && (
          <div className={`text-sm text-center p-3 rounded-lg ${
            isSuccess 
              ? 'bg-green-100 text-green-700 border border-green-300' 
              : 'bg-red-100 text-red-700 border border-red-300'
          }`}>
            {message}
          </div>
        )}
      </form>

      <div className="mt-4 text-xs text-gray-500 text-center">
        By subscribing, you agree to receive our newsletter. You can unsubscribe at any time.
      </div>
    </div>
  );
};

export default MailerLiteSignup;
